import { z } from "zod";

export const ENV_CONSUMERS = [
  "next-app",
  "temporal-worker",
  "jobs-worker",
  "mcp-server",
  "slack-connector",
  "survey-exporter",
  "render-audit-export",
  "render-workflows",
  "llm-cache-service",
] as const;

/**
 * Add every new consumer here and to infisical-targets.ts. The Infisical CI
 * check can only verify consumers that have an explicit target mapping.
 */
export type EnvConsumer = (typeof ENV_CONSUMERS)[number];

const nodeEnvironmentSchema = z.enum(["development", "test", "production"]);
const vercelEnvironmentSchema = z.enum([
  "development",
  "preview",
  "production",
]);

export type DeploymentStage = "dev" | "staging" | "prod";
type NodeEnvironment = z.output<typeof nodeEnvironmentSchema>;
type VercelEnvironment = z.output<typeof vercelEnvironmentSchema>;

export type EnvSchemaContext = {
  nodeEnv: NodeEnvironment | undefined;
  vercelEnv: VercelEnvironment | undefined;
  publicVercelEnv: VercelEnvironment | undefined;
};

export type EnvSchema = z.AnyZodObject;

function parseOptionalContextValue<TValue>(
  name: string,
  schema: z.ZodType<TValue>,
  value: unknown
): TValue | undefined {
  if (value === undefined) return undefined;

  const result = schema.safeParse(value);
  if (result.success) return result.data;

  console.error(
    `Ignoring invalid ${name} while resolving environment schema context.`
  );
  return undefined;
}

export type SchemaDefinition<TSchema extends EnvSchema = EnvSchema> = {
  envConsumers: readonly EnvConsumer[];
  schema: TSchema | ((context: EnvSchemaContext) => TSchema);
};

export function defineEnvSchema<TSchema extends EnvSchema>(
  definition: SchemaDefinition<TSchema>
): SchemaDefinition<TSchema> {
  return definition;
}

export function resolveEnvSchema<TSchema extends EnvSchema>(
  definition: SchemaDefinition<TSchema>,
  context: EnvSchemaContext
): TSchema {
  return typeof definition.schema === "function"
    ? definition.schema(context)
    : definition.schema;
}

export function createRuntimeEnvSchemaContext(
  source: Record<string, unknown>
): EnvSchemaContext {
  const nodeEnv = parseOptionalContextValue(
    "NODE_ENV",
    nodeEnvironmentSchema,
    source.NODE_ENV
  );
  const vercelEnv = parseOptionalContextValue(
    "VERCEL_ENV",
    vercelEnvironmentSchema,
    source.VERCEL_ENV
  );
  const publicVercelEnv = parseOptionalContextValue(
    "NEXT_PUBLIC_VERCEL_ENV",
    vercelEnvironmentSchema,
    source.NEXT_PUBLIC_VERCEL_ENV
  );

  return {
    nodeEnv,
    vercelEnv,
    publicVercelEnv,
  };
}

export function createCiEnvSchemaContext(
  stage: DeploymentStage
): EnvSchemaContext {
  return {
    nodeEnv: stage === "dev" ? "development" : "production",
    vercelEnv:
      stage === "prod"
        ? "production"
        : stage === "staging"
          ? "preview"
          : "development",
    publicVercelEnv:
      stage === "prod"
        ? "production"
        : stage === "staging"
          ? "preview"
          : "development",
  };
}
